/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import isAbortedRequest from '../../helpers/isAbortedRequest';
import { getVariants } from './getVariants';

const findProductById = (list, productId) => (
  list?.find(({ _id }) => String(_id) === String(productId))
);

const resolveProductVariants = (product, existing) => (
  product.variants?.length ? product.variants : (existing?.variants || [])
);

const mergeFetchedProduct = (item, product) => Object.assign(item, product, {
  isLoading: false,
  variants: item.variants?.length ? item.variants : (product.variants || []),
});

const applyFetchedProductToList = (list, product, { insertIfMissing } = {}) => {
  if (!list) return;

  const index = list.findIndex((item) => String(item._id) === String(product._id));
  if (index === -1) {
    if (insertIfMissing) {
      list.push({
        ...product,
        isLoading: false,
        variants: product.variants || [],
      });
    }
    return;
  }

  mergeFetchedProduct(list[index], product);
};

export const getMasterProduct = createAsyncThunk(
  'products/getMasterProduct',
  async ({ productId }, {
    dispatch, getState, extra: { Logger, sdk }, signal,
  }) => {
    try {
      const { data } = await sdk.products.getMasterProduct(productId, signal);
      if (isAbortedRequest(null, signal)) {
        throw new DOMException('Aborted', 'AbortError');
      }
      const productsState = getState().products;
      const currentProduct = findProductById(productsState.items, productId)
        || findProductById(productsState.search?.items, productId);

      let fetchedVariants;
      if (!currentProduct?.variants?.length && !data.variants?.length) {
        const action = await dispatch(getVariants({ _id: productId }));
        if (Array.isArray(action?.payload)) {
          fetchedVariants = action.payload;
        }
      }

      return {
        ...data,
        variants: resolveProductVariants(data, {
          variants: fetchedVariants || currentProduct?.variants,
        }),
      };
    } catch (error) {
      if (isAbortedRequest(error, signal)) throw error;
      Logger.log('UI', 'ToastProductGetFailed');
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    getMasterProduct.pending,
    (state, { meta }) => {
      const { productId } = meta.arg;
      const item = findProductById(state.items, productId);
      if (item) item.isLoading = true;
      state.error = null;
    },
  ).addCase(
    getMasterProduct.fulfilled,
    (state, { payload: product }) => {
      if (!product?._id) return;
      const existing = findProductById(state.items, product._id)
        || findProductById(state.search?.items, product._id);
      const nextProduct = {
        ...product,
        variants: resolveProductVariants(product, existing),
      };
      applyFetchedProductToList(state.items, nextProduct, { insertIfMissing: true });
      applyFetchedProductToList(state.search?.items, nextProduct);
    },
  ).addCase(
    getMasterProduct.rejected,
    (state, { error, meta }) => {
      const { productId } = meta.arg;
      const item = findProductById(state.items, productId);
      if (item) item.isLoading = false;
      if (meta.aborted) return;
      state.error = error;
    },
  );
};
